import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, FileText, Download, Printer, Eye, Edit2, Loader2, X, ShoppingCart, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseAPI, vendorAPI, productAPI, branchAPI, fileAPI, stockAPI, fetchPaged } from '../../services/api';
import Pagination from '../ui/Pagination';
import VendorManagement from './VendorManagement';
import SortableHeader from '../ui/SortableHeader';
import PurchaseOrderForm from './PurchaseOrderForm';
import PurchaseOrderDetail from './PurchaseOrderDetail';
import { getAllowedUOMs, convertRate } from '../../utils/uomConversion';
import { generatePurchaseOrderPDF, printPurchaseOrder } from './purchaseOrderPdfUtils';
import { usePermissions } from '../../hooks/usePermissions';

const statusColors = {
  'Draft': 'bg-gray-100 text-gray-600',
  'Ordered': 'bg-amber-100 text-amber-700',
  'In Transit': 'bg-blue-100 text-blue-700',
  'Partially Received': 'bg-purple-100 text-purple-700',
  'Received': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-700',
};

const PurchaseModule = () => {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [selectedPO, setSelectedPO] = useState(null);
  const [view, setView] = useState('list'); // list, detail, create, edit
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [linkedItems, setLinkedItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const [logoBase64, setLogoBase64] = useState('');
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const { hasPermission } = usePermissions();

  // Preload company logo for PDF generation
  useEffect(() => {
    fetch('/prismoline-logo.png')
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, []);

  const emptyForm = {
    vendor: '', vendor_name: '', items: [{ product: '', name: '', description: '', qty: 0, unit: 'kg', rate: 0, amount: 0, hsn_code: '', gst_rate: 18 }],
    other_charges: [],
    currency: 'INR', amount: 0, inr_value: 0, expected_delivery: '', status: 'Draft',
    branch: user.branch || '', type: 'domestic',
    exchange_rate: 1, notes: '', billing_address: '',
    shipping_address: '', vendor_gst: '', vendor_address: '', company_gst: '',
    payment_terms: '50% Advance, 50% Against Delivery', delivery_terms: '',
    reference_no: '', document_title: '', comments: '',
    payment_milestones: [],
    // Import fields
    loading_port: '', discharge_port: '', dispatch_by: '', incoterms: '',
    etd: '', eta: '', shipping_line: '', bl_awb_number: '',
  };
  const [form, setForm] = useState({ ...emptyForm });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchAll(); }, [filter, search, page, pageSize, sortConfig]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (filter) params.status = filter;
      if (sortConfig.key) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.order; }
      const [result, vendorsData, productsData, branchesData, linksData] = await Promise.all([
        fetchPaged('/purchase', params),
        vendorAPI.getAll().catch(() => []),
        productAPI.getAll().catch(() => []),
        branchAPI.getAll().catch(() => []),
        stockAPI.getLinkedItems().catch(() => []),
      ]);
      setPos(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setVendors(vendorsData);
      setProducts(productsData);
      setBranches(branchesData);
      setLinkedItems(linksData);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const calcTotals = (items, exchangeRate = 1, otherCharges = []) => {
    const amount = items.reduce((s, i) => s + (i.amount || 0), 0);
    const chargesTotal = (otherCharges || []).reduce((s, c) => s + (c.amount || 0), 0);
    return { amount: amount + chargesTotal, inr_value: (amount + chargesTotal) * (exchangeRate || 1) };
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'qty' || field === 'rate' || field === 'gst_rate' ? Number(value) : value };
    if (field === 'qty' || field === 'rate') {
      items[idx].amount = items[idx].qty * items[idx].rate;
    }
    if (field === 'product' && value) {
      const prod = products.find(p => p.id === value);
      if (prod) {
        items[idx].name = prod.name;
        items[idx].unit = prod.buy_unit || prod.unit || 'kg';
        items[idx].rate = prod.cost_price;
        items[idx].hsn_code = prod.hsn_code;
        items[idx].gst_rate = prod.gst_rate;
        items[idx].amount = items[idx].qty * prod.cost_price;
        items[idx]._allowedUOMs = getAllowedUOMs(prod);
        items[idx]._baseUnit = prod.unit;
        items[idx]._baseRate = prod.cost_price;
        items[idx]._conversions = prod.unit_conversions || [];
      }
    }
    // Auto-convert rate when unit changes
    if (field === 'unit' && items[idx].product) {
      const conversions = items[idx]._conversions || [];
      const baseUnit = items[idx]._baseUnit || '';
      const baseRate = items[idx]._baseRate || 0;
      if (baseUnit && baseRate && conversions.length > 0) {
        const newRate = convertRate(conversions, baseUnit, baseRate, value);
        items[idx].rate = newRate;
        items[idx].amount = items[idx].qty * newRate;
      }
    }
    const totals = calcTotals(items, form.exchange_rate, form.other_charges);
    setForm({ ...form, items, ...totals });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { product: '', name: '', qty: 0, unit: 'kg', rate: 0, amount: 0, hsn_code: '', gst_rate: 18 }] });
  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    const items = form.items.filter((_, i) => i !== idx);
    const totals = calcTotals(items, form.exchange_rate, form.other_charges);
    setForm({ ...form, items, ...totals });
  };

  const handlePOProductSelect = (idx, prod) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      product: prod.id, name: prod.name,
      unit: prod.buy_unit || prod.unit || 'kg',
      rate: prod.cost_price || 0, hsn_code: prod.hsn_code || '',
      gst_rate: prod.gst_rate || 18,
      amount: items[idx].qty * (prod.cost_price || 0),
      sku: prod.sku || '',
    };
    items[idx]._allowedUOMs = getAllowedUOMs(prod);
    items[idx]._baseUnit = prod.unit;
    items[idx]._baseRate = prod.cost_price || 0;
    items[idx]._conversions = prod.unit_conversions || [];
    // Auto-convert rate if buy_unit differs from base unit
    const buyUnit = prod.buy_unit || prod.unit;
    if (buyUnit !== prod.unit && (prod.unit_conversions || []).length > 0) {
      items[idx].rate = convertRate(prod.unit_conversions, prod.unit, prod.cost_price || 0, buyUnit);
      items[idx].amount = items[idx].qty * items[idx].rate;
    }
    const totals = calcTotals(items, form.exchange_rate, form.other_charges);
    setForm({ ...form, items, ...totals });
  };

  const handlePOProductClear = (idx) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], product: '', name: '', unit: 'kg', rate: 0, hsn_code: '', gst_rate: 18, amount: 0, _allowedUOMs: [] };
    const totals = calcTotals(items, form.exchange_rate, form.other_charges);
    setForm({ ...form, items, ...totals });
  };

  // Other charges helpers
  const addOtherCharge = () => {
    const charges = [...(form.other_charges || []), { description: '', amount: 0, taxable: true }];
    const totals = calcTotals(form.items, form.exchange_rate, charges);
    setForm({ ...form, other_charges: charges, ...totals });
  };
  const updateOtherCharge = (idx, field, value) => {
    const charges = [...(form.other_charges || [])];
    charges[idx] = { ...charges[idx], [field]: field === 'amount' ? Number(value) : field === 'taxable' ? value : value };
    const totals = calcTotals(form.items, form.exchange_rate, charges);
    setForm({ ...form, other_charges: charges, ...totals });
  };
  const removeOtherCharge = (idx) => {
    const charges = (form.other_charges || []).filter((_, i) => i !== idx);
    const totals = calcTotals(form.items, form.exchange_rate, charges);
    setForm({ ...form, other_charges: charges, ...totals });
  };

  // Linked items lookup for smart PO suggestions
  const linkedMap = {};
  linkedItems.forEach(link => {
    if (!linkedMap[link.product_a_id]) linkedMap[link.product_a_id] = [];
    linkedMap[link.product_a_id].push({ id: link.product_b_id, name: link.product_b_name });
    if (!linkedMap[link.product_b_id]) linkedMap[link.product_b_id] = [];
    linkedMap[link.product_b_id].push({ id: link.product_a_id, name: link.product_a_name });
  });

  const selectVendor = (vendorId) => {
    const v = vendors.find(x => x.id === vendorId);
    if (v) {
      const addrParts = [v.address, v.city, v.state].filter(Boolean);
      const fullAddr = addrParts.join(', ') + (v.pincode ? ` - ${v.pincode}` : '');
      setForm({ ...form, vendor: v.id, vendor_name: v.name, vendor_gst: v.gstin || '', vendor_address: fullAddr || '', currency: v.currency, type: v.type, exchange_rate: v.currency === 'INR' ? 1 : form.exchange_rate });
    }
  };

  const handleVendorSelect = (v) => {
    // Build complete vendor address from all fields
    const addrParts = [v.address, v.city, v.state].filter(Boolean);
    const fullAddr = addrParts.join(', ') + (v.pincode ? ` - ${v.pincode}` : '');
    setForm({ ...form, vendor: v.id, vendor_name: v.name, vendor_gst: v.gstin || '', vendor_address: fullAddr || '', vendor_city: v.city || '', vendor_state: v.state || '', currency: v.currency || 'INR', type: v.type || 'domestic', exchange_rate: v.currency === 'INR' ? 1 : form.exchange_rate });
  };

  const handleVendorClear = () => {
    setForm({ ...form, vendor: '', vendor_name: '', vendor_gst: '', vendor_address: '' });
  };

  const selectBranch = async (branchId) => {
    if (!branchId) { setForm(f => ({ ...f, branch: '' })); return; }
    setForm(f => ({ ...f, branch: branchId }));
    try {
      const addr = await branchAPI.getBillingAddress(branchId);
      setForm(f => ({
        ...f,
        branch: branchId,
        billing_address: addr.billing_address || f.billing_address,
        company_gst: addr.company_gst || f.company_gst,
      }));
    } catch (err) { console.error('Failed to fetch branch address:', err); }
  };

  const handleSubmit = async (asDraft = false) => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate POs
    const submitData = { ...form };
    if (asDraft) {
      submitData.status = 'Draft';
    }
    if (!asDraft && (!form.vendor || !form.expected_delivery || form.items.some(i => !i.name || !i.qty))) {
      alert('Please fill all required fields'); return;
    }
    // For draft, only require vendor
    if (asDraft && !form.vendor) {
      alert('Please select a vendor to save as draft'); return;
    }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      if (view === 'edit' && selectedPO) {
        await purchaseAPI.update(selectedPO.id, submitData);
      } else {
        await purchaseAPI.create(submitData);
      }
      setView('list');
      setForm({ ...emptyForm });
      setSelectedPO(null);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save PO');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const openEdit = (po) => {
    setSelectedPO(po);
    setForm({
      vendor: po.vendor, vendor_name: po.vendor_name, items: (po.items || []).map(i => ({ ...i })),
      currency: po.currency, amount: po.amount, inr_value: po.inr_value,
      expected_delivery: po.expected_delivery, status: po.status,
      branch: po.branch, type: po.type,
      exchange_rate: po.exchange_rate || 1, notes: po.notes || '',
      billing_address: po.billing_address || emptyForm.billing_address,
      shipping_address: po.shipping_address || '', vendor_gst: po.vendor_gst || '', vendor_address: po.vendor_address || '',
      vendor_city: po.vendor_city || '', vendor_state: po.vendor_state || '',
      company_gst: po.company_gst || emptyForm.company_gst,
      payment_terms: po.payment_terms || emptyForm.payment_terms,
      delivery_terms: po.delivery_terms || emptyForm.delivery_terms,
      reference_no: po.reference_no || '',
      document_title: po.document_title || '',
      comments: po.comments || '',
      vendor_pi: po.vendor_pi || '',
      other_charges: po.other_charges || [],
      payment_milestones: po.payment_milestones || [],
      loading_port: po.loading_port || '', discharge_port: po.discharge_port || '',
      dispatch_by: po.dispatch_by || '', incoterms: po.incoterms || '',
      etd: po.etd || '', eta: po.eta || '',
      shipping_line: po.shipping_line || '', bl_awb_number: po.bl_awb_number || '',
    });
    setView('edit');
  };

  const updatePOStatus = async (poId, newStatus) => {
    try {
      await purchaseAPI.updateStatus(poId, newStatus);
      fetchAll();
      if (selectedPO?.id === poId) setSelectedPO({ ...selectedPO, status: newStatus });
    } catch (err) { alert('Failed to update status'); }
  };

  const confirmDraftPO = async (poId) => {
    if (!window.confirm('Confirm this Draft PO and move it to Ordered? This is the manager-approval action.')) return;
    try {
      await purchaseAPI.confirmDraft(poId);
      toast.success('PO confirmed and moved to Ordered.');
      fetchAll();
      if (selectedPO?.id === poId) {
        const fresh = await purchaseAPI.getById(poId);
        setSelectedPO(fresh);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm PO');
    }
  };

  const canConfirmPO = ['Admin', 'Manager'].includes(user.role);

  const generatePDF = (po) => generatePurchaseOrderPDF(po, logoBase64);
  const printPO = (po) => printPurchaseOrder(po);

  const filteredPOs = pos;

  // ===== FORM VIEW (Create / Edit) =====
  if (view === 'create' || view === 'edit') {
    return (
      <PurchaseOrderForm
        view={view} form={form} setForm={setForm} emptyForm={emptyForm} selectedPO={selectedPO}
        vendors={vendors} products={products} branches={branches} user={user}
        submitting={submitting} uploading={uploading} setUploading={setUploading}
        handleSubmit={handleSubmit} handleVendorSelect={handleVendorSelect} handleVendorClear={handleVendorClear}
        selectBranch={selectBranch} handlePOProductSelect={handlePOProductSelect} handlePOProductClear={handlePOProductClear}
        updateItem={updateItem} addItem={addItem} removeItem={removeItem}
        calcTotals={calcTotals} addOtherCharge={addOtherCharge} updateOtherCharge={updateOtherCharge} removeOtherCharge={removeOtherCharge}
        linkedMap={linkedMap} fileAPI={fileAPI} fetchAll={fetchAll}
        onBack={() => { setView('list'); setForm({ ...emptyForm }); setSelectedPO(null); }}
      />
    );
  }

  // ===== DETAIL VIEW =====
  if (view === 'detail' && selectedPO) {
    return (
      <PurchaseOrderDetail
        selectedPO={selectedPO} user={user} uploading={uploading} setUploading={setUploading}
        onBack={() => { setView('list'); setSelectedPO(null); }}
        onEdit={openEdit} generatePDF={generatePDF} printPO={printPO} updatePOStatus={updatePOStatus}
        confirmDraftPO={confirmDraftPO} canConfirmPO={canConfirmPO} canEdit={hasPermission('purchase.create')}
        fileAPI={fileAPI} fetchAll={fetchAll} purchaseAPI={purchaseAPI} setSelectedPO={setSelectedPO}
      />
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="p-5 lg:p-6" data-testid="purchase-module">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Purchase Orders</h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage domestic & import purchase orders</p>
        </div>
        {hasPermission('purchase.create') && (
          <button data-testid="create-po-btn" onClick={() => { setForm({ ...emptyForm }); setView('create'); if (user.branch) selectBranch(user.branch); }} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 transition-all" style={{ backgroundColor: '#24549c' }}>
            <Plus className="w-3.5 h-3.5" /> New Purchase Order
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="po-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search PO # or vendor..." className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {['', 'Draft', 'Ordered', 'In Transit', 'Partially Received', 'Received', 'Cancelled'].map(f => (
            <button key={f} data-testid={`po-filter-${(f || 'all').toLowerCase().replace(' ', '-')}`} onClick={() => { setFilter(f); setPage(1); }} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${filter === f ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} style={filter === f ? { backgroundColor: '#24549c' } : {}}>
              {f || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* PO List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Loading purchase orders...</span>
        </div>
      ) : filteredPOs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No purchase orders found</p>
          <p className="text-xs text-gray-400 mt-1">Create your first PO to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <SortableHeader label="PO Number" sortKey="po_number" currentSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Document Title" />
                  <SortableHeader label="Date" sortKey="created_at" currentSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Vendor" sortKey="vendor_name" currentSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Type" />
                  <SortableHeader label="Amount" sortKey="amount" currentSort={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Delivery" />
                  <SortableHeader label="Status" />
                  <SortableHeader label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredPOs.map(po => (
                  <tr key={po.id} data-testid={`po-row-${po.id}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-900">
                      {po.po_number || po.id?.substring(0, 10)}
                      {(po.revision_number || 0) > 0 && <span className="ml-1 text-[9px] text-purple-600 font-bold bg-purple-50 px-1 py-0.5 rounded" data-testid={`po-rev-badge-${po.id}`}>Rev-{po.revision_number}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-[150px] truncate">{po.document_title || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{po.date}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{po.vendor_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${po.type === 'domestic' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {po.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-900">{po.currency && po.currency !== 'INR' ? `${po.currency} ${po.amount >= 100000 ? `${(po.amount / 100000).toFixed(2)}L` : po.amount?.toLocaleString()}` : (po.inr_value >= 100000 ? `${(po.inr_value / 100000).toFixed(2)}L` : po.inr_value?.toLocaleString())}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{po.expected_delivery}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[po.status] || 'bg-gray-100'}`}>
                        {po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`view-po-${po.id}`} onClick={() => { setSelectedPO(po); setView('detail'); }} className="p-1.5 rounded-lg hover:bg-gray-100" title="View"><Eye className="w-3.5 h-3.5 text-gray-600" /></button>
                        <button onClick={() => openEdit(po)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Edit"><Edit2 className="w-3.5 h-3.5 text-gray-600" /></button>
                        <button onClick={() => generatePDF(po)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Download PDF"><Download className="w-3.5 h-3.5 text-gray-600" /></button>
                        <button onClick={() => printPO(po)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Print"><Printer className="w-3.5 h-3.5 text-gray-600" /></button>
                        {po.status === 'Draft' && canConfirmPO && (
                          <button data-testid={`po-confirm-${po.id}`} onClick={() => confirmDraftPO(po.id)} className="p-1.5 rounded-lg hover:bg-green-50" title="Confirm PO (move to Ordered)">
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">
            Showing {pos.length} of {total} purchase orders
          </div>
        </div>
      )}

      {pos.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
    </div>
  );
};

const PurchasePageWrapper = () => {
  const [activeTab, setActiveTab] = useState('purchase_orders');
  return (
    <div className="p-5 lg:p-6" data-testid="purchase-page">
      <div className="flex gap-2 mb-5">
        <button data-testid="tab-purchase-orders" onClick={() => setActiveTab('purchase_orders')}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition-all ${activeTab === 'purchase_orders' ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={activeTab === 'purchase_orders' ? { backgroundColor: '#24549c' } : {}}>
          <ShoppingCart className="w-3.5 h-3.5" />Purchase Orders
        </button>
        <button data-testid="tab-vendors" onClick={() => setActiveTab('vendors')}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition-all ${activeTab === 'vendors' ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={activeTab === 'vendors' ? { backgroundColor: '#24549c' } : {}}>
          <Building2 className="w-3.5 h-3.5" />Vendor Management
        </button>
      </div>
      {activeTab === 'purchase_orders' && <PurchaseModule />}
      {activeTab === 'vendors' && <VendorManagement />}
    </div>
  );
};

export default PurchasePageWrapper;
